import { classToPlain, plainToClass } from 'class-transformer'
import { validateOrReject, ValidationError } from 'class-validator'
import Papa from 'papaparse'
import { CSVModelInterface } from './CSVModel.interface'
import languageEncoding from 'detect-file-encoding-and-language'

function checkColumns<T extends CSVModelInterface> (Class: new () => T, sampleItem: any) {
  const templateHeader = Object.keys(classToPlain(new Class(), {
    strategy: 'excludeAll',
    excludeExtraneousValues: true,
    enableImplicitConversion: true,
    exposeDefaultValues: false
  }))

  const errors = []
  for (const header of templateHeader) {
    if (!Object.keys(sampleItem).includes(header)) {
      errors.push(`'${header}' column is missing from the CSV`)
    }
  }

  if (errors.length > 0) throw new Error(errors.join('\n'))
}

function checkCSV<T extends CSVModelInterface> (Class: new () => T, sampleItem: any) {
  const templateHeader = Object.keys(classToPlain(new Class(), {
    strategy: 'excludeAll',
    excludeExtraneousValues: true,
    enableImplicitConversion: true,
    exposeDefaultValues: false
  }))

  const sampleHeader = Object.keys(sampleItem)

  const missingColumns = []

  templateHeader.forEach(header => {
    if (!sampleHeader.includes(header)) {
      missingColumns.push(header)
    }
  })

  const errors: string[] = []

  missingColumns.forEach(col => {
    errors.push(`Column '${col}' is missing`)
  })

  return errors
}

export async function convertPlainToClass<T extends CSVModelInterface> (Class: new () => T, plain: any[]): Promise<{ success: boolean; items: T[]; errors: string[] }> {
  const csvColumnErrors = checkCSV(Class, plain[0])
  if (csvColumnErrors.length > 0) {
    return { success: false, items: [], errors: csvColumnErrors }
  }

  const items: T[] = plainToClass(Class, plain, {
    strategy: 'excludeAll',
    excludeExtraneousValues: true,
    enableImplicitConversion: true,
    exposeDefaultValues: false
  })

  checkColumns(Class, plain[0])

  let success = true
  const errors = []
  for (let i = 1; i <= items.length; i++) {
    try {
      await validateOrReject(items[i - 1] as any)
    } catch (error) {
      if (success) success = !success
      const err: ValidationError = error[0]
      const constraintError = Object.values(err.constraints)
      // eslint-disable-next-line dot-notation
      items[i - 1].error = true
      errors.push(`${constraintError} in row ${i}`)
    }
  }

  return { success, items, errors }
}

export async function csvParser<T extends CSVModelInterface> (Class: new () => T, csv: any): Promise<{ success: boolean; items: T[]; errors: string[] }> {
  if (!csv) throw new Error('CSV is not found')

  const fileInfo = await languageEncoding(csv)
  const encoding = (fileInfo.encoding && fileInfo.confidence.encoding > 0.5) ? fileInfo.encoding : 'UTF-8' // set default encoding to UTF-8

  const plain: Record<string, any>[] = await new Promise((resolve) => {
    Papa.parse(csv, {
      header: true,
      skipEmptyLines: true,
      encoding, // added encoding special characters to fix the issue with the csv file
      complete: (res) => {
        resolve(res.data)
      }
    })
  })

  // Clean up header, remove any space at the beginning or end
  const headers = Object.keys(plain[0])
  let cleanedHeadersCount = 0
  const cleanedHeaders = {}
  headers.forEach(header => {
    const cleaned = header.trim()
    if (cleaned !== header) cleanedHeadersCount += 1
    cleanedHeaders[header] = cleaned
  })

  if (cleanedHeadersCount > 0) {
    plain.forEach(row => {
      Object.keys(cleanedHeaders).forEach(originalHeader => {
        const cleanedHeader = cleanedHeaders[originalHeader]
        row[cleanedHeader] = row[originalHeader]
      })
    })
  }

  return await convertPlainToClass(Class, plain)
}
